import type {
  FetchBaseQueryError,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import _mapValues from 'lodash/mapValues';

import type { PaginationSort } from '@models';
import { api } from '@store/api';
import type { RootState } from '@store/index';
import { extractSearchPages } from '@store/search';
import { impactedAppVersionsTag } from '@features/quick-search/quick-search.cache-tags';

import type {
  Application,
  ApplicationCreateRequest,
  ApplicationFilters,
  ApplicationVersion,
  ApplicationUpdateRequest,
  ApplicationVersionCreateRequest,
  ApplicationVersionUpdateRequest,
  Asset,
  AssetsListFilters,
  AssetUpdateRequest,
  ComplianceFilters,
  ContentsListFilters,
  CreateApplicationResponse,
  CreateApplicationVersionResponse,
  GetApplicationResponse,
  GetApplicationVersionResponse,
  GetApplicationVersionsResponse,
  GetApplicationsResponse,
  GetAssetResponse,
  GetAssetsByPackageResponse,
  GetAssetsResponse,
  GetVersionContentsResponse,
  GetVersionPackagesSummaryResponse,
  GetOrUpsertVulnerabilityAnnotationResponse,
  GetVersionPolicyFindingsResponse,
  GetVersionPolicyStatusResponse,
  GetVersionVulnerabilitiesResponse,
  GetVersionVulnerabilitySummaryResponse,
  GetVulnerabilityPackagesResponse,
  ImageNormalizeResponse,
  PackagesSummary,
  UpdateApplicationResponse,
  UpdateApplicationVersionResponse,
  VersionFilters,
  VersionPolicyStatus,
  VulnerabilitiesFilters,
  VulnerabilityAnnotation,
  VulnerabilityAnnotationCreateRequest,
  VulnerabilityAnnotationUpdateRequest,
  VulnerabilitySummary,
} from './applications.models';
import {
  applicationJobsListByAppTag,
  applicationsListTag,
  applicationTag,
  applicationVersionAssetTag,
  applicationVersionAssetsListTag,
  applicationVersionContentsTag,
  applicationVersionPackagesSummaryTag,
  applicationVersionPolicyByAppTag,
  applicationVersionPolicyTag,
  applicationVersionScopedTags,
  applicationVersionsByAppTag,
  applicationVersionTag,
  applicationVersionVulnerabilitiesTag,
  applicationVersionVulnerabilityAnnotationTag,
  applicationVersionVulnerabilityPackagesTag,
  applicationVersionVulnerabilitySummaryTag,
} from './utils/cache-tags';
import {
  serializeApplicationsListFilters,
  serializeAssetsListFilters,
  serializeComplianceFilters,
  serializeContentsListFilters,
  serializeVersionsListFilters,
  serializeVulnerabilitiesFilters,
} from './utils/filters';

export const DEFAULT_APPLICATIONS_SORT: PaginationSort = { name: false };
export const DEFAULT_ASSETS_SORT: PaginationSort = { name: false };
export const DEFAULT_VERSIONS_SORT: PaginationSort = { name: true };

export const applicationsAPI = api.injectEndpoints({
  endpoints: builder => ({
    listApplications: builder.infiniteQuery<
      GetApplicationsResponse,
      { sort?: PaginationSort; filters?: ApplicationFilters },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({ queryArg: { sort, filters }, pageParam }) => ({
        url: `/applications`,
        params: {
          ...(sort && { sort }),
          ...serializeApplicationsListFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: result =>
        result
          ? [
              ...result.pages
                .flatMap(({ data }) => data)
                .map(({ systemMetadata: { id } }) => applicationTag(id)),
              applicationsListTag,
            ]
          : [applicationsListTag],
    }),
    getApplication: builder.query<Application, string>({
      query: appId => ({
        url: `/applications/${appId}`,
      }),
      transformResponse: (response: GetApplicationResponse) => response.data,
      providesTags: (_, __, id) => [applicationTag(id)],
    }),
    listApplicationVersions: builder.infiniteQuery<
      GetApplicationVersionsResponse,
      {
        appId: string;
        sort?: PaginationSort;
        filters?: VersionFilters;
      },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({ queryArg: { appId, sort, filters }, pageParam }) => ({
        url: `/applications/${appId}/versions`,
        params: {
          ...(sort && { sort }),
          ...serializeVersionsListFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (result, _, { appId }) => [
        ...(result
          ? result.pages
              .flatMap(({ data }) => data)
              .map(({ systemMetadata: { id } }) =>
                applicationVersionTag(appId, id),
              )
          : []),
        applicationVersionsByAppTag(appId),
      ],
    }),
    getApplicationVersion: builder.query<
      ApplicationVersion,
      { appId: string; versionId: string }
    >({
      query: ({ appId, versionId }) => ({
        url: `/applications/${appId}/versions/${versionId}`,
      }),
      transformResponse: (response: GetApplicationVersionResponse) =>
        response.data,
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionTag(appId, versionId),
      ],
    }),
    listAssets: builder.infiniteQuery<
      GetAssetsResponse,
      {
        appId: string;
        filters?: AssetsListFilters;
        sort?: PaginationSort;
        versionId: string;
      },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({
        queryArg: { appId, filters, sort, versionId },
        pageParam,
      }) => ({
        url: `/applications/${appId}/versions/${versionId}/assets`,
        params: {
          ...(sort && { sort }),
          ...serializeAssetsListFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (result, _, { appId, versionId }) => [
        ...(result
          ? result.pages
              .flatMap(({ data }) => data)
              .map(({ systemMetadata: { id } }) =>
                applicationVersionAssetTag(appId, versionId, id),
              )
          : []),
        applicationVersionAssetsListTag(appId, versionId),
      ],
    }),
    getAsset: builder.query<
      Asset,
      { appId: string; versionId: string; assetId: string }
    >({
      query: ({ appId, versionId, assetId }) => ({
        url: `/applications/${appId}/versions/${versionId}/assets/${assetId}`,
      }),
      transformResponse: (response: GetAssetResponse) => response.data,
      providesTags: (_, __, { appId, versionId, assetId }) => [
        applicationVersionAssetTag(appId, versionId, assetId),
      ],
    }),
    listVersionContents: builder.infiniteQuery<
      GetVersionContentsResponse,
      {
        appId: string;
        filters?: ContentsListFilters;
        sort?: PaginationSort;
        versionId: string;
      },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({
        queryArg: { appId, filters, sort, versionId },
        pageParam,
      }) => ({
        url: `/applications/${appId}/versions/${versionId}/contents`,
        params: {
          ...(sort && { sort }),
          ...serializeContentsListFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionContentsTag(appId, versionId),
      ],
    }),

    listAssetsByPackage: builder.infiniteQuery<
      GetAssetsByPackageResponse,
      {
        appId: string;
        name: string;
        namespace?: string;
        type: string;
        version: string;
        versionId: string;
      },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({
        queryArg: { appId, name, namespace, type, version, versionId },
        pageParam,
      }) => ({
        url: `/applications/${appId}/versions/${versionId}/assets-by-package`,
        params: {
          name,
          type,
          version,
          ...(namespace && { namespace }),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (result, _, { appId, versionId }) =>
        result
          ? [
              ...result.pages
                .flatMap(({ data }) => data)
                .map(({ systemMetadata: { id } }) =>
                  applicationVersionAssetTag(appId, versionId, id),
                ),
              applicationVersionAssetsListTag(appId, versionId),
            ]
          : [applicationVersionAssetsListTag(appId, versionId)],
    }),

    updateApplication: builder.mutation<
      Application,
      { appId: string } & ApplicationUpdateRequest
    >({
      query: ({ appId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/applications/${appId}`,
      }),
      transformResponse: (response: UpdateApplicationResponse) => response.data,
      invalidatesTags: (_, __, { appId }) => [
        applicationTag(appId),
        applicationVersionPolicyByAppTag(appId),
      ],
    }),
    deleteApplication: builder.mutation<void, string>({
      query: appId => ({
        method: 'DELETE',
        url: `/applications/${appId}`,
      }),
      invalidatesTags: (_, __, appId) => [
        applicationTag(appId),
        applicationsListTag,
        applicationVersionsByAppTag(appId),
        applicationJobsListByAppTag(appId),
      ],
    }),

    createApplication: builder.mutation<Application, ApplicationCreateRequest>({
      query: body => ({
        url: `/applications`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [applicationsListTag],
      transformResponse: (response: CreateApplicationResponse) => response.data,
    }),

    createApplicationVersion: builder.mutation<
      ApplicationVersion,
      { appId: string } & ApplicationVersionCreateRequest
    >({
      query: ({ appId, ...body }) => ({
        body,
        method: 'POST',
        url: `/applications/${appId}/versions`,
      }),
      invalidatesTags: (_, __, { appId }) => [
        applicationVersionsByAppTag(appId),
        applicationTag(appId),
      ],
      transformResponse: (response: CreateApplicationVersionResponse) =>
        response.data,
    }),

    updateApplicationVersion: builder.mutation<
      ApplicationVersion,
      { appId: string; versionId: string } & ApplicationVersionUpdateRequest
    >({
      query: ({ appId, versionId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/applications/${appId}/versions/${versionId}`,
      }),
      invalidatesTags: (_, __, { appId, versionId }) => [
        applicationVersionTag(appId, versionId),
        applicationVersionsByAppTag(appId),
        applicationTag(appId),
      ],
      transformResponse: (response: UpdateApplicationVersionResponse) =>
        response.data,
    }),

    deleteApplicationVersion: builder.mutation<
      void,
      { appId: string; versionId: string }
    >({
      query: ({ appId, versionId }) => ({
        method: 'DELETE',
        url: `/applications/${appId}/versions/${versionId}`,
      }),
      invalidatesTags: (_, __, { appId, versionId }) => [
        applicationTag(appId),
        applicationVersionTag(appId, versionId),
        applicationVersionsByAppTag(appId),
        applicationVersionAssetsListTag(appId, versionId),
        applicationVersionContentsTag(appId, versionId),
        applicationVersionPackagesSummaryTag(appId, versionId),
        applicationVersionVulnerabilitiesTag(appId, versionId),
        applicationVersionVulnerabilitySummaryTag(appId, versionId),
        applicationVersionPolicyTag(appId, versionId),
        applicationJobsListByAppTag(appId),
      ],
    }),

    deleteAsset: builder.mutation<
      void,
      { appId: string; versionId: string; assetId: string }
    >({
      query: ({ appId, versionId, assetId }) => ({
        method: 'DELETE',
        url: `/applications/${appId}/versions/${versionId}/assets/${assetId}`,
      }),
      invalidatesTags: (_, __, { appId, versionId, assetId }) => [
        ...applicationVersionScopedTags(appId, versionId),
        applicationVersionAssetTag(appId, versionId, assetId),
      ],
    }),

    updateAsset: builder.mutation<
      unknown,
      {
        appId: string;
        versionId: string;
        assetId: string;
      } & AssetUpdateRequest
    >({
      async queryFn(
        { appId, versionId, assetId, ...body },
        { getState },
        _extraOptions,
        baseQuery,
      ): Promise<QueryReturnValue<unknown, FetchBaseQueryError, object>> {
        // The PATCH endpoint treats omitted keys as unchanged — deleted
        // annotations must be explicitly set to null.
        const { annotations, ...rest } = body;
        const patchBody: Record<string, unknown> = { ...rest };

        if (annotations) {
          const state = getState() as RootState;

          // Mirror useGetAssetQuery: check listAssets cache first (the
          // common path), then fall back to the getAsset cache.
          const existingAnnotations =
            extractSearchPages(
              applicationsAPI.endpoints.listAssets.select({
                appId,
                sort: DEFAULT_ASSETS_SORT,
                versionId,
              })(state).data,
            ).find(asset => asset.systemMetadata.id === assetId)?.annotations ??
            applicationsAPI.endpoints.getAsset.select({
              appId,
              versionId,
              assetId,
            })(state).data?.annotations ??
            {};

          patchBody.annotations = {
            ..._mapValues(existingAnnotations, () => null),
            ...annotations,
          };
        }

        return baseQuery({
          body: patchBody,
          method: 'PATCH',
          url: `/applications/${appId}/versions/${versionId}/assets/${assetId}`,
        });
      },
      invalidatesTags: (_, __, { appId, versionId, assetId }) => [
        applicationVersionAssetTag(appId, versionId, assetId),
      ],
    }),

    listVersionPolicyFindings: builder.infiniteQuery<
      GetVersionPolicyFindingsResponse,
      {
        appId: string;
        filters?: ComplianceFilters;
        sort?: PaginationSort;
        versionId: string;
      },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({
        queryArg: { appId, filters, sort, versionId },
        pageParam,
      }) => ({
        url: `/applications/${appId}/versions/${versionId}/policy-findings`,
        params: {
          ...(sort && { sort }),
          ...serializeComplianceFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionPolicyTag(appId, versionId),
        applicationVersionPolicyByAppTag(appId),
      ],
    }),

    getVersionPolicyStatus: builder.query<
      VersionPolicyStatus,
      { appId: string; versionId: string }
    >({
      query: ({ appId, versionId }) => ({
        url: `/applications/${appId}/versions/${versionId}/policy-status`,
      }),
      transformResponse: (response: GetVersionPolicyStatusResponse) =>
        response.data,
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionPolicyTag(appId, versionId),
        applicationVersionPolicyByAppTag(appId),
      ],
    }),

    listVersionVulnerabilities: builder.infiniteQuery<
      GetVersionVulnerabilitiesResponse,
      {
        appId: string;
        filters?: VulnerabilitiesFilters;
        sort?: PaginationSort;
        versionId: string;
      },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({
        queryArg: { appId, filters, sort, versionId },
        pageParam,
      }) => ({
        url: `/applications/${appId}/versions/${versionId}/vulnerabilities`,
        params: {
          ...(sort && { sort }),
          ...serializeVulnerabilitiesFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionVulnerabilitiesTag(appId, versionId),
      ],
    }),

    getVersionPackagesSummary: builder.query<
      PackagesSummary,
      { appId: string; filters?: ContentsListFilters; versionId: string }
    >({
      query: ({ appId, filters, versionId }) => ({
        url: `/applications/${appId}/versions/${versionId}/packages-summary`,
        params: serializeContentsListFilters(filters),
      }),
      transformResponse: (response: GetVersionPackagesSummaryResponse) =>
        response.data,
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionPackagesSummaryTag(appId, versionId),
      ],
    }),

    getVersionVulnerabilitySummary: builder.query<
      VulnerabilitySummary,
      { appId: string; filters?: VulnerabilitiesFilters; versionId: string }
    >({
      query: ({ appId, filters, versionId }) => ({
        url: `/applications/${appId}/versions/${versionId}/vulnerabilities-summary`,
        params: serializeVulnerabilitiesFilters(filters),
      }),
      transformResponse: (response: GetVersionVulnerabilitySummaryResponse) =>
        response.data,
      providesTags: (_, __, { appId, versionId }) => [
        applicationVersionVulnerabilitySummaryTag(appId, versionId),
      ],
    }),

    normalizeImageReference: builder.mutation<ImageNormalizeResponse, string>({
      query: pullString => ({
        body: { pullString },
        method: 'POST',
        url: `/images/normalize`,
      }),
      transformResponse: (response: { data: ImageNormalizeResponse }) =>
        response.data,
    }),

    listVulnerabilityPackages: builder.infiniteQuery<
      GetVulnerabilityPackagesResponse,
      { appId: string; versionId: string; vulnerabilityId: string },
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({
        queryArg: { appId, versionId, vulnerabilityId },
        pageParam,
      }) => ({
        url: `/applications/${appId}/versions/${versionId}/vulnerabilities/${vulnerabilityId}/packages`,
        params: {
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (_, __, { appId, versionId, vulnerabilityId }) => [
        applicationVersionVulnerabilityPackagesTag(
          appId,
          versionId,
          vulnerabilityId,
        ),
      ],
    }),

    getVersionVulnerabilityAnnotation: builder.query<
      VulnerabilityAnnotation,
      { annotationId: string; appId: string; versionId: string }
    >({
      query: ({ annotationId, appId, versionId }) => ({
        url: `/applications/${appId}/versions/${versionId}/vulnerability-annotations/${annotationId}`,
      }),
      transformResponse: (
        response: GetOrUpsertVulnerabilityAnnotationResponse,
      ) => response.data,
      providesTags: (_, __, { annotationId, appId, versionId }) => [
        applicationVersionVulnerabilityAnnotationTag(
          appId,
          versionId,
          annotationId,
        ),
      ],
    }),

    createVersionVulnerabilityAnnotation: builder.mutation<
      VulnerabilityAnnotation,
      {
        appId: string;
        versionId: string;
      } & VulnerabilityAnnotationCreateRequest
    >({
      query: ({ appId, versionId, ...body }) => ({
        body,
        method: 'POST',
        url: `/applications/${appId}/versions/${versionId}/vulnerability-annotations`,
      }),
      transformResponse: (
        response: GetOrUpsertVulnerabilityAnnotationResponse,
      ) => response.data,
      invalidatesTags: (_, __, { appId, versionId, vulnerabilityId }) => [
        applicationVersionVulnerabilityPackagesTag(
          appId,
          versionId,
          vulnerabilityId,
        ),
        applicationVersionVulnerabilitiesTag(appId, versionId),
        applicationVersionVulnerabilitySummaryTag(appId, versionId),
        // VEX status drives the compliance tab's filters and the quick search
        // annotation-status filter, so both of those caches follow the change.
        applicationVersionPolicyTag(appId, versionId),
        impactedAppVersionsTag(vulnerabilityId),
      ],
    }),

    updateVersionVulnerabilityAnnotation: builder.mutation<
      VulnerabilityAnnotation,
      {
        annotationId: string;
        appId: string;
        versionId: string;
        vulnerabilityId: string;
      } & VulnerabilityAnnotationUpdateRequest
    >({
      query: ({
        annotationId,
        appId,
        versionId,
        vulnerabilityId,
        ...body
      }) => ({
        body,
        method: 'PATCH',
        url: `/applications/${appId}/versions/${versionId}/vulnerability-annotations/${annotationId}`,
      }),
      transformResponse: (
        response: GetOrUpsertVulnerabilityAnnotationResponse,
      ) => response.data,
      invalidatesTags: (
        _,
        __,
        { annotationId, appId, versionId, vulnerabilityId },
      ) => [
        applicationVersionVulnerabilityAnnotationTag(
          appId,
          versionId,
          annotationId,
        ),
        applicationVersionVulnerabilityPackagesTag(
          appId,
          versionId,
          vulnerabilityId,
        ),
        applicationVersionVulnerabilitiesTag(appId, versionId),
        applicationVersionVulnerabilitySummaryTag(appId, versionId),
        // VEX status drives the compliance tab's filters and the quick search
        // annotation-status filter, so both of those caches follow the change.
        applicationVersionPolicyTag(appId, versionId),
        impactedAppVersionsTag(vulnerabilityId),
      ],
    }),

    deleteVersionVulnerabilityAnnotation: builder.mutation<
      void,
      {
        annotationId: string;
        appId: string;
        versionId: string;
        vulnerabilityId: string;
      }
    >({
      query: ({ annotationId, appId, versionId }) => ({
        method: 'DELETE',
        url: `/applications/${appId}/versions/${versionId}/vulnerability-annotations/${annotationId}`,
      }),
      invalidatesTags: (
        _,
        __,
        { annotationId, appId, versionId, vulnerabilityId },
      ) => [
        applicationVersionVulnerabilityAnnotationTag(
          appId,
          versionId,
          annotationId,
        ),
        applicationVersionVulnerabilityPackagesTag(
          appId,
          versionId,
          vulnerabilityId,
        ),
        applicationVersionVulnerabilitiesTag(appId, versionId),
        applicationVersionVulnerabilitySummaryTag(appId, versionId),
        // VEX status drives the compliance tab's filters and the quick search
        // annotation-status filter, so both of those caches follow the change.
        applicationVersionPolicyTag(appId, versionId),
        impactedAppVersionsTag(vulnerabilityId),
      ],
    }),
  }),
});

export const {
  useCreateApplicationMutation,
  useCreateApplicationVersionMutation,
  useCreateVersionVulnerabilityAnnotationMutation,
  useDeleteApplicationMutation,
  useDeleteApplicationVersionMutation,
  useDeleteAssetMutation,
  useDeleteVersionVulnerabilityAnnotationMutation,
  useGetVersionPackagesSummaryQuery,
  useGetVersionPolicyStatusQuery,
  useGetVersionVulnerabilityAnnotationQuery,
  useGetVersionVulnerabilitySummaryQuery,
  useListApplicationsInfiniteQuery,
  useListApplicationVersionsInfiniteQuery,
  useListAssetsByPackageInfiniteQuery,
  useListAssetsInfiniteQuery,
  useListVersionContentsInfiniteQuery,
  useListVersionPolicyFindingsInfiniteQuery,
  useListVersionVulnerabilitiesInfiniteQuery,
  useListVulnerabilityPackagesInfiniteQuery,
  useNormalizeImageReferenceMutation,
  useUpdateApplicationMutation,
  useUpdateApplicationVersionMutation,
  useUpdateAssetMutation,
  useUpdateVersionVulnerabilityAnnotationMutation,
  endpoints: {
    listApplications: {
      useInfiniteQueryState: useListApplicationsInfiniteQueryState,
    },
    getApplication: {
      useQuery: useGetApplicationEndpointQuery,
      useQueryState: useGetApplicationQueryState,
    },
    getApplicationVersion: {
      useQuery: useGetApplicationVersionEndpointQuery,
      useQueryState: useGetApplicationVersionQueryState,
    },
    listApplicationVersions: {
      useInfiniteQueryState: useListApplicationVersionsInfiniteQueryState,
    },
    listAssets: { useInfiniteQueryState: useListAssetsInfiniteQueryState },
    getAsset: { useQuery: useGetAssetEndpointQuery },
    getVersionPolicyStatus: {
      useQueryState: useGetVersionPolicyStatusQueryState,
    },
  },
} = applicationsAPI;
